'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, TrendDown, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/useToast';
import { useUserLogged } from '@/hooks/useUserLogged';
import { isOwner, ownerOnly } from '@/lib/rbac';
import {
  getCreditUsage,
  routerTopupVerify,
  type CreditUsage,
} from '@/apis/routerCredits';
import { AddRouterCreditsDialog } from './AddRouterCreditsDialog';

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Animate a number toward `target`. Respects prefers-reduced-motion (jumps instantly). */
function useCountUp(target: number, durationMs = 450): number {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const from = fromRef.current;
    if (reduce || from === target) {
      fromRef.current = target;
      setValue(target);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // ease-out-quart
      const eased = 1 - Math.pow(1 - t, 4);
      setValue(from + (target - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return value;
}

export function BillingModelRouterSection() {
  const { organization, user } = useUserLogged();
  const orgUuid = organization?.OrgUUID ?? user?.OrganizationUUID ?? '';
  const userUuid = user?.UserUUID ?? '';
  const isAdmin = isOwner(user?.Role);

  const toast = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['routerCreditUsage', orgUuid];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => getCreditUsage(orgUuid),
    enabled: orgUuid.length > 0,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });

  // All hooks are above this line. Now the early returns are safe.
  if (!orgUuid) return null;
  if (isLoading) return <SectionShell loading />;
  if (isError || !data) return <SectionShell errored />;

  return (
    <LoadedSection
      usage={data}
      orgUuid={orgUuid}
      userUuid={userUuid}
      isAdmin={isAdmin}
      onBalance={(balanceUsd) =>
        queryClient.setQueryData<CreditUsage>(queryKey, (prev) =>
          prev ? { ...prev, BalanceUsd: balanceUsd } : prev
        )
      }
      onRefetch={() => queryClient.invalidateQueries({ queryKey })}
      toast={toast}
    />
  );
}

function SectionShell({
  loading,
  errored,
}: {
  loading?: boolean;
  errored?: boolean;
}) {
  return (
    <div className="mb-8 overflow-hidden rounded-lg border border-ds-border-subtlest bg-ds-surface-white p-6">
      <h2 className="ds-text-title-sm-semibold text-ds-text-darker">
        Model Router
      </h2>
      {loading ? (
        <div className="mt-4 space-y-3 animate-pulse">
          <div className="h-9 w-40 rounded bg-ds-gray-subtler" />
          <div className="h-4 w-72 rounded bg-ds-gray-subtler" />
        </div>
      ) : errored ? (
        <p className="ds-text-body-sm-medium mt-3 text-ds-danger-default">
          Could not load your Model Router balance. Try refreshing.
        </p>
      ) : null}
    </div>
  );
}

type ToastFn = ReturnType<typeof useToast>;

function LoadedSection({
  usage,
  orgUuid,
  userUuid,
  isAdmin,
  onBalance,
  onRefetch,
  toast,
}: {
  usage: CreditUsage;
  orgUuid: string;
  userUuid: string;
  isAdmin: boolean;
  onBalance: (balanceUsd: number) => void;
  onRefetch: () => void;
  toast: ToastFn;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const verifiedRef = useRef(false);

  const balance = usage.BalanceUsd;
  const paused = balance <= 0;
  const animated = useCountUp(balance);

  // Verify-on-return: credit the wallet the instant the user lands back from Checkout,
  // then strip the query params so a refresh can't re-trigger it.
  useEffect(() => {
    const topup = params?.get('topup');
    const sessionId = params?.get('session_id');
    if (verifiedRef.current || !topup) return;
    verifiedRef.current = true;

    if (topup === 'success' && sessionId) {
      routerTopupVerify(orgUuid, sessionId)
        .then((res) => {
          if (res.Credited) {
            onBalance(res.BalanceUsd);
            toast({
              title: 'Credits added',
              description: 'Your Model Router balance is topped up.',
              status: 'success',
            });
          }
          onRefetch();
        })
        .catch(() => {
          // Verify failed — Stripe may have collected the charge but our server
          // couldn't confirm it. Surface a clean, actionable message and refetch
          // so the balance card reflects the source of truth once state settles.
          toast({
            title: "Couldn't verify your top-up",
            description:
              "The payment may still be processing — refresh in a moment. If credits don't appear, contact support.",
            status: 'error',
          });
          onRefetch();
        })
        .finally(() => {
          if (pathname) router.replace(pathname);
        });
    } else if (topup === 'cancelled') {
      toast({ title: 'Checkout cancelled', status: 'info' });
      if (pathname) router.replace(pathname);
    } else if (pathname) {
      // e.g. topup=success with no session_id, or an unexpected value — still strip the
      // params so a refresh can't re-enter this effect with a stale flag.
      router.replace(pathname);
    }
  }, [params, orgUuid, pathname, router, onBalance, onRefetch, toast]);

  return (
    <div className="mb-8 overflow-hidden rounded-lg border border-ds-border-subtlest bg-ds-surface-white p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="ds-text-title-sm-semibold text-ds-text-darker">
            Model Router
          </h2>
          <p className="ds-text-body-sm-medium text-ds-text-subtle">
            Pay-as-you-go credits. You keep 80% of what routing saves you versus
            Opus.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setDialogOpen(true)}
          disabled={!isAdmin}
          title={isAdmin ? undefined : ownerOnly('add credits')}
        >
          Add credits
        </Button>
      </div>

      {/* Paused banner */}
      {paused && (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-lg border border-ds-danger-subtlest bg-ds-danger-subtlest px-4 py-3">
          <div className="flex items-center gap-2">
            <Warning
              size={18}
              weight="fill"
              className="shrink-0 text-ds-danger-default"
            />
            <span className="ds-text-body-sm-medium text-ds-text-darker">
              You&apos;ve reached your routing limit. Recharge to resume
              routing.
            </span>
          </div>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="ds-text-body-sm-medium flex shrink-0 items-center gap-1 text-ds-text-darker hover:underline"
            >
              Recharge Now
              <ArrowRight size={14} />
            </button>
          )}
        </div>
      )}

      {/* Balance + savings */}
      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="ds-text-body-sm-medium text-ds-text-subtle">
            Balance
          </span>
          <span className="ds-text-title-md-semibold tabular-nums text-ds-text-darker">
            {formatUsd(Math.max(0, animated))}
          </span>
        </div>
        {usage.SavedUsd > 0 && (
          <div className="flex items-center gap-1.5 rounded-md bg-ds-brand-subtlest px-2.5 py-1 text-ds-brand-subtle">
            <TrendDown size={14} weight="bold" />
            <span className="ds-text-body-xs-semibold tabular-nums">
              Saved {formatUsd(usage.SavedUsd)}
              {usage.SavedPct > 0 ? ` (${usage.SavedPct}%)` : ''} vs Opus
            </span>
          </div>
        )}
      </div>

      <p className="ds-text-body-sm-medium mt-4 border-t border-ds-border-subtlest pt-4 text-ds-text-subtle">
        Credits never expire. Pause anytime — unused balance stays yours.
      </p>

      <AddRouterCreditsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        orgUuid={orgUuid}
        userUuid={userUuid}
        onCredited={(newBalance) => {
          onBalance(newBalance);
          onRefetch();
        }}
      />
    </div>
  );
}
